/**
 * Feasibility starts on its own (sales feedback 2026-10-22: "I want the tool to
 * do all the work"). A first message that reads like a feasibility question,
 * from Home's Ask DANI or the chat's own composer, goes out with DANI's
 * feasibility rules already in its history, so the first reply is a check, not
 * a general answer followed by a button. A file attached with the composer's
 * own paperclip in a feasibility chat (an email, a PDF, a screenshot) is read
 * by the brief reader (useFeasibilityFiles reads several at once).
 *
 * Internal only and behind the feasibility-dani flag: the caller checks both.
 */
import { feasibilityApi } from '@/@feasibility-dani/api';
import { tagConversationPurpose } from '@/lib/credits/tag-conversation-purpose';
import {
    BRIEF_FILE_TYPES,
    MAX_BRIEF_BYTES,
    MAX_BRIEF_CHARS,
    type BriefFile,
} from '@/lib/feasibility/contracts/brief';
import type { StrategyPrimerResponse } from '@/lib/feasibility/contracts/strategy';
import { handoffPrompt, hasFeasibilityRules } from '@/lib/feasibility/dani/primer-marks';
import { visitsListForDani } from '@/lib/feasibility/dani/visits-brief';

import { saveFileAs, saveListsIn } from '../files/upload';
import { fileBriefForDani, writeFileDrafts } from './file-drafts';

/** A message that reads like a feasibility question. */
export const ASKS_FEASIBILITY =
    /\bfeasib|\bcan we (survey|reach|field|get)\b|\bhow many (completes|people could|could we)|\bsurvey (them|these|those|that group)\b|\bincidence\b|\b\d[\d,]*\s*(completes|finished surveys|n=)|\b(bid|rfp|rfq)\b|\bquote\b.*\b(survey|study|completes)\b|\bcan we (do|run|deliver|hit)\b/i;

/**
 * A message that asks for the people at places, not an estimate (M4S-5904):
 * panelist IDs, who passed or visited, a waypoint pull.
 */
export const ASKS_PEOPLE =
    /\bpanelist ids?\b|\b(ids|list) of (the )?(panelists|people)\b|\bwho (passed|visited|drove|walked|went)\b|\bpassed (by|these|those|the)\b|\bdrive-?bys?\b|\bwaypoints?\b|\bvisit history\b|\bretroactive\b/i;

/**
 * A message that asks for a survey to be written, set up or run: it names what to
 * ask ("asking what they came in to buy"), or asks for the survey itself. That is
 * Studio's survey author's work (DANI rule 7.1), even when it also gives completes
 * and days (UAT F41: "I need an in-store survey ... asking ... 30 completes in 7
 * days" became a feasibility check that lost its chain). The written survey's own
 * card checks feasibility.
 */
export const ASKS_FOR_SURVEY =
    /\b(asking|ask (them|people|shoppers|respondents|panelists)|questions? (about|on|like)|questionnaire)\b|\b(write|build|create|draft|program|set up|setup|make|design)\b[^.?!]{0,40}\b(survey|questionnaire|study|concept test|test)\b|\b(need|want|run|field|launch)\b[^.?!]{0,20}\b(an?|the)\b[^.?!]{0,25}\bsurvey\b/i;

/** Says feasibility in so many words: always a check, whatever else it asks. */
const SAYS_FEASIBLE = /\bfeasib|\bhow many (completes|people could|could we)\b/i;

export function asksFeasibility(text: string): boolean {
    if (ASKS_PEOPLE.test(text)) return true;
    if (!ASKS_FEASIBILITY.test(text)) return false;
    return SAYS_FEASIBLE.test(text) || !ASKS_FOR_SURVEY.test(text);
}

/** A file a saved list can be made from: a table with each place's latitude and longitude. */
const LIST_FILE = /\.(csv|xlsx|xls|txt)$/i;

/** A file the brief reader takes (email, PDF, Word, Excel, CSV, text, screenshot). */
export function isBriefFile(name: string): boolean {
    const ext = name.toLowerCase().match(/\.[a-z]+$/)?.[0] ?? '';
    return (BRIEF_FILE_TYPES as readonly string[]).includes(ext);
}

/** A file as the brief reader takes it: text the browser reads, or the bytes. */
export async function briefFile(f: File): Promise<BriefFile | string> {
    const ext = f.name.toLowerCase().match(/\.[a-z]+$/)?.[0] ?? '';
    if (!(BRIEF_FILE_TYPES as readonly string[]).includes(ext))
        return `${f.name}: attach ${BRIEF_FILE_TYPES.join(', ')}.`;
    if (f.size > MAX_BRIEF_BYTES) return `${f.name} is over 10 MB.`;
    if (ext === '.eml' || ext === '.txt')
        return { name: f.name, size: f.size, text: (await f.text()).slice(0, MAX_BRIEF_CHARS) };
    const bytes = new Uint8Array(await f.arrayBuffer());
    let bin = '';
    for (let i = 0; i < bytes.length; i += 0x8000)
        bin += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
    return { name: f.name, size: f.size, base64: btoa(bin) };
}

/** The rules as one text, for a conversation that already has history. */
export function rulesText(primer: StrategyPrimerResponse): string {
    return primer.messages
        .filter((m) => m.role === 'user')
        .map((m) => m.content.map((c) => c.text).join('\n'))
        .join('\n\n');
}

type MessageLike = { role?: unknown; content: unknown };

export interface RoutedSend {
    prompt: string;
    /** The rules as pre-seeded history, for a new conversation. */
    previousMessages?: StrategyPrimerResponse['messages'];
    /** What the person's bubble shows when the prompt carries Studio's reading of a file. */
    displayMessage?: string;
}

interface RouteFeasibilityArgs {
    content: string;
    conversationId: string;
    isNewConversation: boolean;
    messages: MessageLike[];
    file?: File | null;
    api?: Pick<typeof feasibilityApi, 'strategyPrimer' | 'brief' | 'saveList'>;
}

/**
 * How a send goes out, or null when it is not a feasibility send (it then goes
 * as it always did). Reads an attached brief file first, so DANI gets Studio's
 * drafts instead of the raw text.
 *
 * Credits (M4S-5912): a new conversation that carries the rules is Feasibility's
 * usage, so it is tagged before its first message, as "Work it out with DANI"
 * does (`dani/page.tsx`). The wait is capped and a tag that fails leaves it
 * counted as chat. Only once routing has worked: a send whose routing throws
 * goes to DANI without the rules. An ongoing chat that turns to feasibility is
 * not tagged: the first tag wins, and its earlier usage was chat.
 */
export async function routeFeasibilitySend(args: RouteFeasibilityArgs): Promise<RoutedSend | null> {
    const routed = await routeSend(args);
    if (routed?.previousMessages)
        await tagConversationPurpose(args.conversationId, { purpose: 'feasibility' });
    return routed;
}

async function routeSend(args: RouteFeasibilityArgs): Promise<RoutedSend | null> {
    const api = args.api ?? feasibilityApi;
    const hasRules = hasFeasibilityRules(args.messages);
    const asks = asksFeasibility(args.content);
    const file = args.file && isBriefFile(args.file.name) ? args.file : null;
    // A file in a feasibility chat, or with a feasibility ask, is a brief.
    const readFile = file != null && (hasRules || asks);
    if (!readFile && !(asks && !hasRules)) return null;

    const primer = hasRules ? null : await api.strategyPrimer();
    // New conversations carry the rules as history; an ongoing one gets them in the prompt.
    const previousMessages = primer && args.isNewConversation ? primer.messages : undefined;
    const inlineRules = primer && !args.isNewConversation ? rulesText(primer) : null;

    // The people at the file's places (M4S-5904): saved as a list, pulled by DANI's card.
    if (readFile && file && ASKS_PEOPLE.test(args.content) && LIST_FILE.test(file.name)) {
        const list = await saveFileAs(file, 'placements', { api })
            // Not a table of coordinates: read it as a brief below.
            .catch(() => null);
        if (list) {
            const display = args.content.trim();
            return {
                prompt: handoffPrompt(display, visitsListForDani(list, display, inlineRules)),
                previousMessages,
                displayMessage: display,
            };
        }
    }
    if (readFile && file) {
        const read = await briefFile(file);
        if (typeof read === 'string') throw new Error(read);
        const result = await api.brief({ files: [read] });
        // A station, zip or ID list read as a brief is kept in Files too.
        void saveListsIn([file], api).catch(() => undefined);
        writeFileDrafts(args.conversationId, {
            briefId: result.briefId,
            files: [file.name],
            drafts: result.drafts,
        });
        const display = args.content.trim() || `Check feasibility for ${file.name}`;
        return {
            prompt: handoffPrompt(display, fileBriefForDani(result, [file.name], inlineRules)),
            previousMessages,
            displayMessage: display,
        };
    }
    return {
        prompt: inlineRules ? handoffPrompt(args.content, inlineRules) : args.content,
        previousMessages,
        ...(inlineRules ? { displayMessage: args.content } : {}),
    };
}
